import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Modal,
  Input,
  Space,
  Select,
  Switch,
} from 'antd'
import { PlusOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons'
import axios from 'axios'

import {
  GetRoomMaster, // To load Rooms dropdown
  GetBedMaster,
  CreateBedMaster,
  UpdateBedMaster,
  GetRoomMasterById,
  BranchMaster,
  UpdateBedStatus,
} from '../../../AllAPI/AdminAPI'

import { toast } from 'react-toastify'

const BedMaster = () => {
  const storedBranchId = sessionStorage.getItem('selected_branch')

  const [data, setData] = useState([])
  const [rooms, setRooms] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  // useState inputs
  const [roomId, setRoomId] = useState('')
  const [bedNo, setBedNo] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)

  const [modalStatus, setModalStatus] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [isRoomBooked, setIsRoomBooked] = useState(false)

  useEffect(() => {
    fetchRooms()
    fetchBeds()
  }, [])

  // Load rooms (for dropdown)
  const fetchRooms = async () => {
    try {
      const res = await axios.get(`${GetRoomMasterById}/${storedBranchId}`)
      setRooms(res.data)
    } catch (err) {
      toast.error('Failed to load rooms')
    }
  }

  // Load beds
  const fetchBeds = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${GetBedMaster}/${storedBranchId}`)

      const formatted = res.data.map((item, index) => ({
        key: item.bed_id,
        index: index + 1,
        bed_no: item.bed_no,
        roomName: item.RoomNo?.room_no ?? 'N/A',
        status: item.status === 1 ? 'Active' : 'Inactive',
        room_id: item.room_id,
        isBooked: item.bedbooked && item.bedbooked.length > 0,
      }))

      setData(formatted)
    } catch (err) {
      toast.error('Failed to load beds')
    } finally {
      setLoading(false) // 👉 Stop loader
    }
  }

  // Open modal for Add/Edit
  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingId(record.key)
      setRoomId(record.room_id)
      setBedNo(record.bed_no)
      setModalStatus(record.status === 'Active')
      setIsRoomBooked(record.isBooked) 
    } else {
      setEditingId(null)
      setRoomId('')
      setBedNo('')
      setModalStatus(true)
      setIsRoomBooked(false)
    }
    setIsModalOpen(true)
  }

  const handleModalStatusChange = async (checked) => {
    setModalStatus(checked)
    setUpdatingStatus(true)

    try {
      const payload = {
        bed_id: editingId,
        status: checked ? 1 : 2,
      }

      await axios.put(UpdateBedStatus, payload)
      toast.success('Status updated')

      // update UI table instantly
      setData((prev) =>
        prev.map((item) =>
          item.key === editingId
            ? { ...item, status: checked ? 'Active' : 'Inactive' }
            : item,
        ),
      )
    } catch (err) {
      toast.error('Failed to update status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  // Save / Update
  const handleSave = async () => {
    if (!roomId) return toast.error('Please select a room')
    if (!bedNo.trim()) return toast.error('Please enter bed number')
    setLoading(true)

    const payload = {
      branch_id: parseInt(storedBranchId),
      room_id: roomId,
      bed_no: bedNo,
    }

    try {
      if (editingId) {
        const res = await axios.put(`${UpdateBedMaster}/${editingId}`, payload)
        if (res.data === 'UPDATED') {
          toast.success('Bed updated successfully')
        }
      } else {
        const res = await axios.post(CreateBedMaster, payload)
        if (res.data === 'SAVED') {
          toast.success('Bed added successfully')
        }
      }

      fetchBeds()
      setIsModalOpen(false)
      setEditingId(null)
      setRoomId('')
      setBedNo('')
    } catch (err) {
      toast.error('Failed to save bed')
    } finally {
      setLoading(false) // 👉 Stop loader
    }
  }

  // Search filter
  const filteredData = data.filter((item) =>
    (item.bed_no + '').toLowerCase().includes(searchText.toLowerCase()),
  )

  const columns = [
    { title: 'Sr No', dataIndex: 'index' },
    { title: 'Room', dataIndex: 'roomName' },
    { title: 'Bed', dataIndex: 'bed_no' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, record) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '12px',
            fontWeight: '600',
            color: record.status === 'Active' ? 'green' : 'red',
            background: record.status === 'Active' ? '#eaffea' : '#ffeaea',
          }}
        >
          {record.status === 'Active' ? 'ON' : 'OFF'}
        </span>
      ),
    },
    {
      title: 'Actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            // disabled={record.isBooked} 
            onClick={() => handleOpenModal(record)}
          />
        </Space>
      ),
    },
  ]

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>
        <div className="col-12 col-md-8">
          <div className="header-row">
            <h2 className="page-title">Bed Master</h2>
          </div>

          <div className="search-bar">
            <Input
              placeholder="Search bed..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />

            <Button
              type="primary"
              className="mt-2 mt-md-0"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              style={{ marginLeft: 10 }}
            >
              Add Bed
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            bordered
            loading={loading}
            scroll={{ x: 'max-content' }}
          />
        </div>
        <div className="col-12 col-md-2"></div>
      </div>

      {/* MODAL */}
      <Modal
        title={editingId ? 'Edit Bed' : 'Add Bed'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            Save
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 15 }}>
          <label>Select Room</label>
          <Select
            value={roomId}
            onChange={setRoomId}
            placeholder="Select Room"
            style={{ width: '100%' }}
          >
            {rooms.map((r) => (
              <Select.Option key={r.room_id} value={r.room_id}>
                {r.room_no}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: 15 }}>
          <label>Bed Number</label>
          <Input
            value={bedNo}
            onChange={(e) => setBedNo(e.target.value)}
            placeholder="Enter bed number"
          />
        </div>

        {editingId && (
          <div>
            <label>Status</label>
            <br />
            <Switch
              checked={modalStatus}
              loading={updatingStatus}
              onChange={handleModalStatusChange}
              checkedChildren="ON"
              unCheckedChildren="OFF"
              disabled={isRoomBooked}
            />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default BedMaster
